/* eslint-disable react-refresh/only-export-components */
import type {ReactElement} from 'react';
import {useContext, useState} from 'react';
import {Icon} from '@Components/icon-v2/icon';
import {IconSize} from '@Components/icon-v2/icon.types';
import {Text, TextSize} from '@Components/text';
import {emailMakerContext} from '@EmailMakerReact/email-maker-react.context';
import {setComponentStyle, useStyleProperty} from '@EmailMakerReact/lib/email-maker-component-style-library';
import type {EmailMakerControl, EmailMakerControlProps} from '../../right-panel-registry.types';
import {DEFAULT_FONT, DEFAULT_FONT_SELECTOR_DESCRIPTOR, FONT_LIST, FONTS_WARNING_DESCRIPTOR} from './font-selector-control.types';
import type {FontEntry, FontSelectorDescriptor} from './font-selector-control.types';
import {createDescriptorControl} from '../create-descriptor-control';
import {InfoBox} from '../info-box-control/components/info-box/info-box';
import styles from './font-selector-control.module.scss';

function renderSelectedCheck(isSelected: boolean): ReactElement | null {
  if (!isSelected) {
    return null;
  }
  return <Icon aria-hidden className="icon" icon="icon-check" size={IconSize.SIZE_ICON_16} />;
}

function resolveCurrentFontValue(rawValue: string, fallbackValue: string): string {
  if (rawValue !== '') {
    return rawValue;
  }
  return fallbackValue !== '' ? fallbackValue : DEFAULT_FONT.value;
}

interface FontSelectorControlInnerProps extends EmailMakerControlProps {
  descriptor: FontSelectorDescriptor;
}

function FontSelectorControlInner({component, descriptor}: FontSelectorControlInnerProps): ReactElement {
  const {editor} = useContext(emailMakerContext);
  const [isPanelOpen, setIsPanelOpen] = useState(false);

  const rawValue = useStyleProperty(editor, component, descriptor.property);
  const fallbackValue = useStyleProperty(editor, component, descriptor.fallbackProperty ?? '');

  const currentFontValue = resolveCurrentFontValue(rawValue, fallbackValue);
  const currentFont: FontEntry = FONT_LIST.find((f) => f.value === currentFontValue) ?? DEFAULT_FONT;

  function handleFontSelect(font: FontEntry): void {
    if (!editor) {
      return;
    }
    setComponentStyle(editor, {
      property: descriptor.property,
      value: font.value,
      component,
    });
    if (descriptor.onFontChange) {
      descriptor.onFontChange(component, editor);
    }
    setIsPanelOpen(false);
  }

  function renderFontList(): ReactElement | null {
    if (!isPanelOpen) {
      return null;
    }
    return (
      <div className={styles.panel}>
        <InfoBox descriptor={FONTS_WARNING_DESCRIPTOR} />
        <Text val={window.i18next.t('pmwjs_fonts')} size={TextSize.MD} bold className={styles.fontsTitle} />
        <ul className={styles.fontList}>
          {FONT_LIST.map((font) => (
            <li className={`${styles.fontItem} ${font.value === currentFontValue ? styles.fontItemSelected : ''}`} key={font.id}>
              <button
                className={styles.fontItemButton}
                onClick={(): void => {
                  handleFontSelect(font);
                }}
                style={{fontFamily: font.value}}
                type="button"
              >
                {font.name}
                {renderSelectedCheck(font.value === currentFontValue)}
              </button>
            </li>
          ))}
        </ul>
      </div>
    );
  }

  return (
    <div className={styles.wrapper}>
      <div className={styles.labelRow}>
        <Text val={window.i18next.t(descriptor.labelKey)} size={TextSize.MD} className={styles.label} />
        <button
          aria-expanded={isPanelOpen}
          className={styles.selectorButton}
          onClick={(): void => {
            setIsPanelOpen((prev) => !prev);
          }}
          style={{fontFamily: currentFont.value}}
          type="button"
        >
          {currentFont.name}
        </button>
      </div>
      {renderFontList()}
    </div>
  );
}

export function makeFontSelectorControl(descriptor: FontSelectorDescriptor = DEFAULT_FONT_SELECTOR_DESCRIPTOR): EmailMakerControl {
  return createDescriptorControl(FontSelectorControlInner, descriptor);
}
